import { useEffect, useRef, useState } from "react";
import { UploadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Modal } from "../pages/Contacts";

export function ImportDialog({ onClose, onDone }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    api.importLatest()
      .then(({ job: j }) => {
        if (j && ["processing", "applying", "preview_ready", "failed"].includes(j.status)) setJob(j);
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (!job || !["processing", "applying"].includes(job.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const j = await api.importStatus(job.id);
        setJob(j);
        if (!["processing", "applying"].includes(j.status)) clearInterval(pollRef.current);
      } catch { /* keep polling */ }
    }, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [job?.id, job?.status]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const j = await api.importPreview(f);
      setJob(j);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the file");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      await api.importApply(job.id);
      setJob({ ...job, status: "applying", error: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start");
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      await api.importApply(job.id);
      setJob({ ...job, status: "applying", error: null, progress: job.progress });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resume — upload the file again");
    } finally { setBusy(false); }
  };

  const s = job?.stats || {};
  const a = job?.applied || {};

  return (
    <Modal title="Import contacts" subtitle="Upload a CSV or Excel export (e.g. from Hunter.io). Existing contacts are updated by email match; new people are added. Unrecognised columns are ignored." onClose={onClose} wide>
      {!job && !checking && (
        <label data-testid="import-dropzone" className="block border border-dashed border-zinc-300 rounded-lg p-10 text-center cursor-pointer hover:bg-zinc-50">
          <input type="file" accept=".xlsx,.csv" onChange={onFile} className="hidden" data-testid="import-file"/>
          <UploadSimple size={28} className="mx-auto text-zinc-400"/>
          <div className="mt-2 text-sm font-medium">{busy ? "Uploading…" : "Click to select a .csv or .xlsx file"}</div>
          <div className="text-xs text-zinc-500 mt-1">
            Recognised columns: First name, Last name, Email address, Company, Job title, Website, LinkedIn URL.
            <br/>New contacts need first name, last name, email and company — everything else is optional.
          </div>
        </label>
      )}

      {job?.status === "processing" && (
        <div className="py-8 text-center space-y-3" data-testid="import-processing">
          <div className="text-sm font-medium">Analysing your file against the contact database…</div>
          <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden max-w-sm mx-auto">
            <div className="h-full bg-zinc-900 transition-all" style={{ width: `${job.progress?.total ? Math.max(5, Math.round((job.progress.done / job.progress.total) * 100)) : 5}%` }}/>
          </div>
          <div className="text-xs text-zinc-500">{(job.progress?.total || 0).toLocaleString()} rows read — nothing is changed until you press Import.</div>
        </div>
      )}

      {job?.status === "preview_ready" && (
        <div className="space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-4 gap-2">
            <Stat label="Rows in file" value={s.rows_total} />
            <Stat label="New contacts" value={s.to_create} tone="emerald" />
            <Stat label="Will be updated" value={s.to_update} tone="sky" />
            <Stat label="No changes" value={s.unchanged} tone="zinc" />
          </div>
          {s.skipped > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2" data-testid="import-skipped-note">
              {s.skipped.toLocaleString()} row{s.skipped === 1 ? "" : "s"} will be skipped — new contacts need first name, last name, email and company.
            </p>
          )}
          <div>
            <div className="text-xs font-medium text-zinc-600 uppercase tracking-wider mb-2">Field updates on existing contacts</div>
            <div className="grid grid-cols-3 gap-2">
              <Stat small label="Job titles added" value={s.titles_added} />
              <Stat small label="Job titles updated" value={s.titles_updated} />
              <Stat small label="Names filled in" value={s.names_filled} />
              <Stat small label="Companies filled in" value={s.companies_filled} />
              <Stat small label="Websites set" value={s.websites_set} />
              <Stat small label="LinkedIn URLs set" value={s.linkedins_set} />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-600 uppercase tracking-wider mb-2">Automatic tagging from job titles</div>
            <div className="grid grid-cols-2 gap-2">
              <Stat small label="Role tags (role: …)" value={s.role_tags} />
              <Stat small label="Seniority tags (level: …)" value={s.level_tags} />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => setJob(null)} className="px-3 py-1.5 text-sm rounded-md border border-zinc-200 hover:bg-zinc-50" data-testid="import-back">Choose another file</button>
            <button data-testid="import-apply" disabled={busy || (!s.to_create && !s.to_update)} onClick={apply} className="modal-btn-primary">
              {busy ? "Starting…" : `Import (${(s.to_create || 0).toLocaleString()} new · ${(s.to_update || 0).toLocaleString()} updates)`}
            </button>
          </div>
        </div>
      )}

      {job?.status === "applying" && (
        <div className="py-8 text-center space-y-3" data-testid="import-applying">
          <div className="text-sm font-medium">Importing…</div>
          <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden max-w-sm mx-auto">
            <div className="h-full bg-zinc-900 transition-all" style={{ width: `${job.progress?.total ? Math.round((job.progress.done / job.progress.total) * 100) : 0}%` }}/>
          </div>
          <div className="text-xs text-zinc-500">{(job.progress?.done || 0).toLocaleString()} of {(job.progress?.total || 0).toLocaleString()} rows processed — safe to close this window, progress continues in the background</div>
        </div>
      )}

      {job?.status === "done" && (
        <div className="py-6 text-center space-y-3" data-testid="import-done">
          <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-600"/>
          <div className="text-sm font-medium">Import complete</div>
          <div className="text-xs text-zinc-600">
            {(a.contacts_created || 0).toLocaleString()} new contact{(a.contacts_created || 0) === 1 ? "" : "s"} added · {(a.contacts_updated || 0).toLocaleString()} updated
            {a.rows_skipped > 0 && <> · {a.rows_skipped.toLocaleString()} skipped</>}
          </div>
          <button data-testid="import-close-done" onClick={onDone} className="modal-btn-primary">Done</button>
        </div>
      )}

      {job?.status === "failed" && (
        <div className="py-6 text-center space-y-3" data-testid="import-failed">
          <WarningCircle size={32} className="mx-auto text-rose-600"/>
          <div className="text-sm font-medium">Something went wrong</div>
          <div className="text-xs text-rose-600 max-w-md mx-auto">{job.error}</div>
          <div className="flex items-center justify-center gap-2 pt-1">
            {job.resumable !== false && (
              <button data-testid="import-retry" disabled={busy} onClick={retry} className="modal-btn-primary">
                {busy ? "Resuming…" : "Retry — resume where it left off"}
              </button>
            )}
            <button data-testid="import-start-over" disabled={busy} onClick={() => setJob(null)} className="px-3 py-1.5 text-xs font-medium rounded-md border border-zinc-300 hover:bg-zinc-50">
              Upload a different file
            </button>
          </div>
          <p className="text-[11px] text-zinc-400">Changes already applied are kept — retrying never duplicates work.</p>
        </div>
      )}
    </Modal>
  );
}

const Stat = ({ label, value, tone = "zinc", small }) => (
  <div className={`rounded-md border px-3 ${small ? "py-1.5" : "py-2.5"} ${tone === "emerald" ? "border-emerald-200 bg-emerald-50/60" : tone === "sky" ? "border-sky-200 bg-sky-50/60" : "border-zinc-200 bg-zinc-50/60"}`}>
    <div className={`${small ? "text-sm" : "text-lg"} font-semibold tabular-nums text-zinc-900`}>{(value ?? 0).toLocaleString()}</div>
    <div className="text-[10px] text-zinc-500 leading-tight">{label}</div>
  </div>
);
